import React, { useEffect, useState } from "react";
import { Plus, Trash2, CheckCircle2, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/ArticleCards";
import { ARTICLE_TYPES, SPECIALTIES, ACADEMIC_DEGREES, DEPARTMENTS } from "@/lib/constants";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import ComboTextField from "@/components/ComboTextField";

const blankAuthor = () => ({
  first_name: "", middle_name: "", last_name: "", second_last_name: "",
  academic_degree: "", email: "", orcid: "", affiliation: "", department: "",
  institution: "", city: "", country: "", order: 1, corresponding: false,
});

const declarationKeys = [
  "original", "not_published_elsewhere", "all_authors_approve",
  "conflicts_disclosed", "funding_disclosed", "ethics_approved",
  "consent_obtained", "no_fee_understood", "apc_understood",
  "privacy_notice",
];

export default function Submit() {
  const { t } = useI18n();
  const [apc, setApc] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setApc(String(r.data.apc_amount_usd ?? ""))).catch(() => {}); }, []);
  const ta = (k) => t(k).replace("{amount}", apc);
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [corresp, setCorresp] = useState({
    first_name: "", middle_name: "", last_name: "", second_last_name: "",
    email: "", phone: "", orcid: "", affiliation: "", department: "",
    institution: "", city: "", country: "",
  });

  const [info, setInfo] = useState({
    title: "", short_title: "", article_type: "Original Research",
    abstract: "", keywords: "", main_specialty: "",
  });

  const [authors, setAuthors] = useState([{ ...blankAuthor(), corresponding: true }]);
  const [author1Photo, setAuthor1Photo] = useState(null);
  const [author1PhotoPreview, setAuthor1PhotoPreview] = useState("");
  const [files, setFiles] = useState({ manuscript: null, figures: [], supplementary: [] });
  const [decl, setDecl] = useState(Object.fromEntries(declarationKeys.map((k) => [k, false])));

  const addAuthor = () => setAuthors([...authors, { ...blankAuthor(), order: authors.length + 1 }]);
  const removeAuthor = (i) => setAuthors(authors.filter((_, idx) => idx !== i));
  const updateAuthor = (i, key, val) => {
    setAuthors(authors.map((a, idx) => (idx === i ? { ...a, [key]: val } : a)));
  };

  const onAuthor1Photo = (file) => {
    setAuthor1Photo(file);
    setAuthor1PhotoPreview(file ? URL.createObjectURL(file) : "");
  };

  const allDeclared = declarationKeys.every((k) => decl[k]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allDeclared) {
      toast.error(t("submit.need_declarations"));
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(corresp).forEach(([k, v]) => fd.append(`corresponding_${k}`, v || ""));
      Object.entries(info).forEach(([k, v]) => fd.append(k, v || ""));
      fd.append("authors_json", JSON.stringify(authors));
      fd.append("declarations_json", JSON.stringify(decl));
      if (files.manuscript) fd.append("manuscript", files.manuscript);
      (files.figures || []).forEach((f) => fd.append("figures", f));
      (files.supplementary || []).forEach((f) => fd.append("supplementary", f));
      if (author1Photo) fd.append("author1_photo", author1Photo);
      const { data } = await api.post("/submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Submission failed. Please review your form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="py-16 md:py-24" data-testid="submission-confirmation">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <CheckCircle2 className="w-14 h-14 text-[#2BA6A6] mx-auto" />
          <h1 className="font-serif text-3xl md:text-4xl text-[#001F3F] mt-6 mb-2">{t("submit.confirmation_title")}</h1>
          <div className="w-10 h-[2px] bg-[#2BA6A6] mx-auto"></div>
          <p className="mt-6 font-serif text-base text-[#001F3F] leading-relaxed">
            {t("submit.confirmation_body")}
          </p>
          <div className="mt-8 bg-[#F5F7FA] border border-gray-200 p-5 inline-block">
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2BA6A6]">{t("submit.your_id")}</div>
            <div className="font-serif text-2xl text-[#001F3F] mt-2" data-testid="submission-id">{submitted.submission_id}</div>
          </div>
          <p className="mt-8 text-sm text-gray-600" data-testid="submit-apc-note">
            {ta("submit.apc_note")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24" data-testid="submit-page">
      <div className="max-w-4xl mx-auto px-6">
        <SectionTitle subtitle={t("submit.subtitle")}>
          {t("submit.title")}
        </SectionTitle>

        <form onSubmit={handleSubmit} className="space-y-12" data-testid="submit-form">
          {/* Section 1 */}
          <FormSection title={t("submit.s1")}>
            <Grid>
              <Field label={t("submit.field.first_name")} required value={corresp.first_name} onChange={(v) => setCorresp({ ...corresp, first_name: v })} testId="corr-first" />
              <Field label={t("submit.field.middle_name")} value={corresp.middle_name} onChange={(v) => setCorresp({ ...corresp, middle_name: v })} testId="corr-middle" />
              <Field label={t("submit.field.last_name")} required value={corresp.last_name} onChange={(v) => setCorresp({ ...corresp, last_name: v })} testId="corr-last" />
              <Field label={t("submit.field.second_last_name")} value={corresp.second_last_name} onChange={(v) => setCorresp({ ...corresp, second_last_name: v })} />
              <Field label={t("submit.field.email")} type="email" required value={corresp.email} onChange={(v) => setCorresp({ ...corresp, email: v })} testId="corr-email" />
              <Field label={t("submit.field.phone")} value={corresp.phone} onChange={(v) => setCorresp({ ...corresp, phone: v })} />
              <Field label={t("submit.field.orcid")} value={corresp.orcid} onChange={(v) => setCorresp({ ...corresp, orcid: v })} />
              <Field label={t("submit.field.affiliation")} value={corresp.affiliation} onChange={(v) => setCorresp({ ...corresp, affiliation: v })} />
              <ComboTextField label={t("submit.field.department")} value={corresp.department} onChange={(v) => setCorresp({ ...corresp, department: v })} options={DEPARTMENTS} placeholder={t("submit.choose")} />
              <Field label={t("submit.field.institution")} value={corresp.institution} onChange={(v) => setCorresp({ ...corresp, institution: v })} />
              <Field label={t("submit.field.city")} value={corresp.city} onChange={(v) => setCorresp({ ...corresp, city: v })} />
              <Field label={t("submit.field.country")} value={corresp.country} onChange={(v) => setCorresp({ ...corresp, country: v })} />
            </Grid>
          </FormSection>

          {/* Section 2 */}
          <FormSection title={t("submit.s2")}>
            <Grid>
              <Field full label={t("submit.field.title")} required value={info.title} onChange={(v) => setInfo({ ...info, title: v })} testId="info-title" />
              <Field full label={t("submit.field.short_title")} value={info.short_title} onChange={(v) => setInfo({ ...info, short_title: v })} />
              <Select label={t("submit.field.article_type")} required value={info.article_type} onChange={(v) => setInfo({ ...info, article_type: v })} options={ARTICLE_TYPES} testId="info-type" />
              <ComboTextField label={t("submit.field.specialty")} value={info.main_specialty} onChange={(v) => setInfo({ ...info, main_specialty: v })} options={SPECIALTIES} testId="info-specialty" placeholder={t("submit.choose")} />
              <Textarea full label={t("submit.field.abstract")} value={info.abstract} onChange={(v) => setInfo({ ...info, abstract: v })} rows={6} testId="info-abstract" />
              <Field full label={t("submit.field.keywords")} value={info.keywords} onChange={(v) => setInfo({ ...info, keywords: v })} />
            </Grid>
          </FormSection>

          {/* Section 3: Authors */}
          <FormSection title={t("submit.s3")}>
            {authors.map((a, i) => (
              <div key={i} className="border border-gray-200 p-5 mb-4 bg-white">
                <div className="flex justify-between items-center mb-4">
                  <div className="font-sans text-[11px] uppercase tracking-[0.2em] font-semibold text-[#2BA6A6]">Author {i + 1}</div>
                  {authors.length > 1 && (
                    <button type="button" onClick={() => removeAuthor(i)} className="text-red-500 text-xs flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  )}
                </div>
                {i === 0 && (
                  <div className="flex items-center gap-4 mb-5 pb-5 border-b border-gray-100" data-testid="author1-photo-block">
                    <div className="shrink-0">
                      {author1PhotoPreview ? (
                        <img
                          src={author1PhotoPreview}
                          alt="Author 1"
                          className="w-16 h-16 rounded-full object-cover border border-gray-200"
                          data-testid="author1-photo-preview"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-[#F5F7FA] border border-dashed border-gray-300 flex items-center justify-center">
                          <ImageIcon className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">
                        {t("submit.field.author_photo")}
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => onAuthor1Photo(e.target.files?.[0] || null)}
                        className="w-full text-xs font-sans file:mr-3 file:px-4 file:py-2 file:border-0 file:bg-[#001F3F] file:text-white file:cursor-pointer file:hover:bg-[#2BA6A6] file:transition-colors"
                        data-testid="author1-photo-input"
                      />
                      <p className="text-[11px] text-gray-500 mt-1.5">{t("submit.author_photo_hint")}</p>
                    </div>
                  </div>
                )}
                <Grid>
                  <Field label={t("submit.field.first_name")} value={a.first_name} onChange={(v) => updateAuthor(i, "first_name", v)} />
                  <Field label={t("submit.field.middle_name")} value={a.middle_name} onChange={(v) => updateAuthor(i, "middle_name", v)} />
                  <Field label={t("submit.field.last_name")} value={a.last_name} onChange={(v) => updateAuthor(i, "last_name", v)} />
                  <Field label={t("submit.field.second_last_name")} value={a.second_last_name} onChange={(v) => updateAuthor(i, "second_last_name", v)} />
                  <ComboTextField label={t("submit.field.academic_degree")} value={a.academic_degree} onChange={(v) => updateAuthor(i, "academic_degree", v)} options={ACADEMIC_DEGREES} placeholder="MD, PhD…" />
                  <Field label={t("submit.field.email")} type="email" value={a.email} onChange={(v) => updateAuthor(i, "email", v)} />
                  <Field label={t("submit.field.orcid")} value={a.orcid} onChange={(v) => updateAuthor(i, "orcid", v)} />
                  <Field label={t("submit.field.affiliation")} value={a.affiliation} onChange={(v) => updateAuthor(i, "affiliation", v)} />
                  <ComboTextField label={t("submit.field.department")} value={a.department} onChange={(v) => updateAuthor(i, "department", v)} options={DEPARTMENTS} placeholder={t("submit.choose")} />
                  <Field label={t("submit.field.institution")} value={a.institution} onChange={(v) => updateAuthor(i, "institution", v)} />
                  <Field label={t("submit.field.city")} value={a.city} onChange={(v) => updateAuthor(i, "city", v)} />
                  <Field label={t("submit.field.country")} value={a.country} onChange={(v) => updateAuthor(i, "country", v)} />
                  <Field label={t("submit.field.author_order")} type="number" value={a.order} onChange={(v) => updateAuthor(i, "order", parseInt(v) || 1)} />
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-[#001F3F]">
                      <input type="checkbox" checked={!!a.corresponding} onChange={(e) => updateAuthor(i, "corresponding", e.target.checked)} className="accent-[#2BA6A6]" />
                      {t("submit.corresponding_checkbox")}
                    </label>
                  </div>
                </Grid>
              </div>
            ))}
            <button type="button" onClick={addAuthor} className="text-sm font-sans uppercase tracking-wider text-[#2BA6A6] flex items-center gap-2 hover:text-[#001F3F]" data-testid="add-author">
              <Plus className="w-4 h-4" /> {t("submit.add_author")}
            </button>
          </FormSection>

          {/* Section 4: Files */}
          <FormSection title={t("submit.s4")}>
            <Grid>
              <FileField label={t("submit.field.manuscript")} onChange={(f) => setFiles({ ...files, manuscript: f })} testId="file-manuscript" />
              <FileField multi label={t("submit.field.figures")} onChange={(arr) => setFiles({ ...files, figures: arr })} />
              <FileField multi label={t("submit.field.supplementary")} onChange={(arr) => setFiles({ ...files, supplementary: arr })} />
            </Grid>
          </FormSection>

          {/* Section 5: Declarations */}
          <FormSection title={t("submit.s5")}>
            <div className="space-y-3 bg-[#F5F7FA] border border-gray-200 p-5">
              {declarationKeys.map((k) => (
                <label key={k} className="flex items-start gap-3 text-sm font-sans text-[#001F3F]">
                  <input
                    type="checkbox"
                    checked={!!decl[k]}
                    onChange={(e) => setDecl({ ...decl, [k]: e.target.checked })}
                    className="mt-1 accent-[#2BA6A6]"
                    data-testid={`decl-${k}`}
                  />
                  <span data-testid={`decl-text-${k}`}>{ta(`submit.decl.${k}`)}</span>
                </label>
              ))}
            </div>
          </FormSection>

          <div className="text-center">
            <button
              type="submit"
              disabled={submitting || !allDeclared}
              className="bg-[#001F3F] text-white px-10 py-4 text-sm font-sans uppercase tracking-[0.18em] font-semibold hover:bg-[#2BA6A6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-manuscript-btn"
            >
              {submitting ? t("submit.button_submitting") : t("submit.button")}
            </button>
            {!allDeclared && (
              <div className="text-xs text-gray-500 mt-3">{t("submit.need_declarations")}</div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <section>
      <h3 className="font-serif text-xl text-[#001F3F] mb-1">{title}</h3>
      <div className="w-8 h-[2px] bg-[#2BA6A6] mb-6"></div>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, required, type = "text", value, onChange, full, testId }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">
        {label}{required && <span className="text-[#2BA6A6]"> *</span>}
      </label>
      <input
        type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
        data-testid={testId}
      />
    </div>
  );
}

function Textarea({ label, value, onChange, rows = 4, full, testId }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
        className="w-full px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
        data-testid={testId}
      />
    </div>
  );
}

function Select({ label, required, value, onChange, options, full, placeholder, testId }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">
        {label}{required && <span className="text-[#2BA6A6]"> *</span>}
      </label>
      <select required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
        data-testid={testId}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function FileField({ label, onChange, multi, testId }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">{label}</label>
      <input
        type="file"
        multiple={!!multi}
        onChange={(e) => onChange(multi ? Array.from(e.target.files || []) : (e.target.files?.[0] || null))}
        className="w-full text-xs font-sans file:mr-3 file:px-4 file:py-2 file:border-0 file:bg-[#001F3F] file:text-white file:cursor-pointer file:hover:bg-[#2BA6A6] file:transition-colors"
        data-testid={testId}
      />
    </div>
  );
}
